/* The Optimizer's settings form: which services and categories to offer, and turning
 * what was picked into what `tt_optimizer_settings.priorities` holds.
 *
 * Online availability is the whole gain, so only what is bookable online can do anything
 * as a priority (js/rules/optimizer.js ignores the rest). The flag comes from the proxy
 * when the page can read it, and from the admin's own ticks when it can't. `flags` below
 * is { serviceId: boolean }, or null for not read.
 *
 * What is kept in the row is a choice ("the Facials category"), and a category's members
 * are worked out from the catalogue at each save. Their length comes from the catalogue
 * again at each check (prioritiesFor in the optimizer function).
 *
 * Pure: arguments in, plain objects out.
 */

function isLive(service) { return service.deleted !== true; }

/* What the picker lists: [{ key, categoryId, label, services }], categories by name,
 * then anything with none. Once flags are known, only what is bookable online. */
function catalogueGroups(services, names, flags) {
  var groups = {};
  (services || []).filter(isLive).forEach(function (s) {
    if (flags && flags[s.id] !== true) return;
    var id = s.category_id == null ? null : s.category_id;
    var key = id == null ? 'none' : String(id);
    if (!groups[key]) {
      groups[key] = {
        key: key,
        categoryId: id,
        label: id == null ? 'No category' : ((names && names[id]) || 'Category ' + id),
        services: []
      };
    }
    groups[key].services.push(s);
  });
  return Object.keys(groups).map(function (k) { return groups[k]; }).sort(function (a, b) {
    if ((a.categoryId == null) !== (b.categoryId == null)) return a.categoryId == null ? 1 : -1;
    return String(a.label).localeCompare(String(b.label));
  });
}

function draftId(kind, refId) { return (kind === 'category' ? 'c:' : 's:') + refId; }

/* Pick or unpick. `choice` is { kind, refId, label }. */
function toggleDraft(draft, choice) {
  var id = draftId(choice.kind, choice.refId);
  if (draft.some(function (d) { return d.id === id; })) {
    return draft.filter(function (d) { return d.id !== id; });
  }
  return draft.concat([{ id: id, kind: choice.kind, refId: choice.refId, label: choice.label }]);
}

/* The form's picks, and the ticks, from what is saved. */
function draftFromStored(stored) {
  var draft = [];
  var hand = {};
  (Array.isArray(stored) ? stored : []).forEach(function (p) {
    var refId = p && typeof p.id === 'string' ? Number(p.id.split(':')[1]) : NaN;
    if (!Number.isInteger(refId) || (p.kind !== 'service' && p.kind !== 'category')) return;
    draft.push({ id: p.id, kind: p.kind, refId: refId, label: p.label || '' });
    (p.services || []).forEach(function (s) { hand[s.id] = s.availableInOnlineBooking === true; });
  });
  return { draft: draft, hand: hand };
}

function membersOf(pick, services) {
  if (pick.kind === 'category') {
    return (services || []).filter(function (s) { return isLive(s) && s.category_id === pick.refId; });
  }
  var row = (services || []).find(function (s) { return s.id === pick.refId; });
  return [row || { id: pick.refId }];
}

/* What gets saved. The proxy's word wins, and a service it didn't list is not online. The
 * ticks count only when the flags couldn't be read. */
function buildPriorities(draft, services, flags, hand) {
  return (draft || []).map(function (pick) {
    return {
      id: pick.id,
      label: String(pick.label || '').trim(),
      kind: pick.kind,
      services: membersOf(pick, services).map(function (s) {
        var out = { id: s.id };
        if (s.duration_minutes > 0) out.minutes = s.duration_minutes;
        out.availableInOnlineBooking = flags ? flags[s.id] === true : !!hand && hand[s.id] === true;
        return out;
      })
    };
  });
}

function priorityWarnings(priorities) {
  return (priorities || []).filter(function (p) {
    return !(p.services || []).some(function (s) { return s.availableInOnlineBooking === true; });
  }).map(function (p) {
    return p.label + ' has nothing bookable online, so the Optimizer will ignore it.';
  });
}

/* A sentence for each priority with no services at all, which would save as nothing. */
function emptyProblems(priorities) {
  return (priorities || []).filter(function (p) { return !(p.services || []).length; }).map(function (p) {
    return p.label + ' has no services in the catalogue, so it can\'t be saved.';
  });
}

function fieldProblems(form) {
  var out = [];
  if (!Number.isInteger(form.check_minutes) || form.check_minutes < 1) {
    out.push('The check interval has to be a whole number of minutes, 1 or more.');
  }
  if (!Number.isInteger(form.cutoff_minutes) || form.cutoff_minutes < 0) {
    out.push('The cut-off has to be a whole number of minutes, 0 or more.');
  }
  return out;
}

export {
  buildPriorities, catalogueGroups, draftFromStored, emptyProblems, fieldProblems, priorityWarnings, toggleDraft
};
